import React from 'react';
import { DollarSign, ShoppingBag, Package, Users, TrendingUp, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { adminProducts, adminCustomers, adminOrders, monthlySales, adminCategories } from '../adminData';

function DashboardHome() {
  const totalRevenue = adminOrders.reduce((sum, o) => sum + o.total, 0);
  const totalOrders = adminOrders.length;
  const totalStock = adminProducts.reduce((sum, p) => sum + p.stock, 0);
  const totalCustomers = adminCustomers.length;
  const maxSales = Math.max(...monthlySales.map(m => m.value));
  const totalCategoryRevenue = adminCategories.reduce((sum, c) => sum + c.revenue, 0);

  const statCards = [
    { label: 'Total Revenue', value: `${totalRevenue.toLocaleString()} SAR`, icon: DollarSign, trend: '+12.5%', up: true },
    { label: 'Total Orders', value: totalOrders, icon: ShoppingBag, trend: '+8.2%', up: true },
    { label: 'Products in Stock', value: totalStock, icon: Package, trend: '-3.1%', up: false },
    { label: 'Total Customers', value: totalCustomers, icon: Users, trend: '+15.4%', up: true },
  ];

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>Dashboard Overview</h1>
          <p>Welcome back, here's what's happening with your store today.</p>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="admin-stats-grid">
        {statCards.map((stat, i) => (
          <div key={i} className="admin-stat-card">
            <div className="admin-stat-top">
              <div className="admin-stat-icon-box">
                <stat.icon size={22} />
              </div>
              <span className={`admin-stat-trend ${stat.up ? 'up' : 'down'}`}>
                {stat.up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                {stat.trend}
              </span>
            </div>
            <h3>{stat.value}</h3>
            <p>{stat.label}</p>
          </div>
        ))}
      </div>

      <div className="admin-grid-2">
        {/* Sales Bar Chart */}
        <div className="admin-card">
          <div className="admin-card-header">
            <h3><TrendingUp size={18} /> Monthly Sales Revenue</h3>
          </div>
          <div className="admin-bar-chart">
            {monthlySales.map((m, i) => (
              <div key={i} className="admin-bar-col">
                <div className="admin-bar-value">{(m.value / 1000).toFixed(1)}k</div>
                <div className="admin-bar-track">
                  <div
                    className="admin-bar-fill"
                    style={{ height: `${(m.value / maxSales) * 100}%` }}
                  ></div>
                </div>
                <div className="admin-bar-label">{m.month}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Category Distribution Donut */}
        <div className="admin-card">
          <div className="admin-card-header">
            <h3>Revenue by Category</h3>
          </div>
          <div className="admin-donut-wrapper">
            <div
              className="admin-donut"
              style={{
                background: `conic-gradient(${adminCategories.map((c, i) => {
                  const prevPercent = adminCategories.slice(0, i).reduce((s, x) => s + (x.revenue / totalCategoryRevenue) * 360, 0);
                  const percent = (c.revenue / totalCategoryRevenue) * 360;
                  return `${c.color} ${prevPercent}deg ${prevPercent + percent}deg`;
                }).join(', ')})`
              }}
            >
              <div className="admin-donut-center">
                <span>{totalCategoryRevenue.toLocaleString()}</span>
                <small>Total SAR</small>
              </div>
            </div>
            <div className="admin-donut-legend">
              {adminCategories.map((c, i) => (
                <div key={i} className="admin-legend-item">
                  <span className="admin-legend-dot" style={{ background: c.color }}></span>
                  <span className="admin-legend-name">{c.name}</span>
                  <span className="admin-legend-value">{c.revenue.toLocaleString()} SAR</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="admin-grid-2">
        {/* Recent Orders */}
        <div className="admin-card">
          <div className="admin-card-header">
            <h3>Recent Orders</h3>
          </div>
          <div className="admin-mini-table">
            {adminOrders.slice(0, 5).map(order => (
              <div key={order.id} className="admin-mini-row">
                <div className="admin-mini-col">
                  <strong>#{order.id}</strong>
                  <span>{order.customer}</span>
                </div>
                <span className={`admin-status-pill status-${order.status.toLowerCase()}`}>
                  {order.status}
                </span>
                <strong className="admin-mini-total">{order.total} SAR</strong>
              </div>
            ))}
          </div>
        </div>

        {/* Top Products */}
        <div className="admin-card">
          <div className="admin-card-header">
            <h3>Top Selling Products</h3>
          </div>
          <div className="admin-mini-table">
            {[...adminProducts].sort((a, b) => b.sold - a.sold).slice(0, 5).map(p => (
              <div key={p.id} className="admin-mini-row">
                <div className="admin-mini-col">
                  <strong>{p.name}</strong>
                  <span>{p.category}</span>
                </div>
                <span className="admin-sold-badge">{p.sold} sold</span>
                <strong className="admin-mini-total">{p.price} SAR</strong>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardHome;
